import { isHtmlElement } from '../../libs/preact-entry'
import { h, Component, ComponentChildren, render } from 'preact'

interface Props {
  into: HTMLElement,
  idx: number,
  isMovable: boolean,
  id: number,
}

/*
   Some of the code is borrowed from preact-portal and is used to
   render child components in the elements that's passed as 'into' prop.
   This component also takes care of re-ordering and delete animations
 */

export default class OrderAnimator extends Component<Props> {
  isMounted: boolean = false
  remote?: Element
  into?: HTMLElement

  componentDidMount() {
    this.isMounted = true
    this.renderLayer = this.renderLayer.bind(this)
    this.renderLayer()
  }

  componentWillUnmount () {
    this.renderLayer(false)
    this.isMounted = false
    if (this.remote && this.remote.parentNode) this.remote.parentNode.removeChild(this.remote)
    this.props.into.style.transition = 'height 0.2s'
    this.props.into.style.height = '0'
    setTimeout(() => {
      if (this.props.into && this.props.into.parentNode) this.props.into.parentNode.removeChild(this.props.into)
    }, 200)
  }

  renderLayer (show = true) {
    if (!this.isMounted) return

    if (this.props.into !== this.into) {
      this.into = this.props.into
      if (this.into && this.remote) {
        this.remote = render(<PortalProxy />, this.into, this.remote)
      }
    }

    this.remote = render((
      <PortalProxy>
        { show && this.props.children || null }
      </PortalProxy>
    ), this.into, this.remote)
  }

  componentDidUpdate (newProps: Props) {
    const keys = Object.keys(newProps) as Array<keyof Props>
    keys.some(i => {
      if (newProps[i] !== this.props[i]) {
        setTimeout(this.renderLayer)
        return true
      }
      return false
    })

    if (!this.props.isMovable) return
    if (newProps.idx <= this.props.idx) return

    const parentEl = this.props.into
    const page = parentEl.parentElement
    if (!page) return
      if (isHtmlElement(parentEl.previousElementSibling)) {
        const other = parentEl.previousElementSibling
        const currentPosition = parentEl.getBoundingClientRect()
        const currentPositionOther = other.getBoundingClientRect()
        page.insertBefore(parentEl, other)
        const newPosition = parentEl.getBoundingClientRect()
        const newPositionOther = other.getBoundingClientRect()
        parentEl.style.height = `${currentPosition.height}px`
        other.style.height = `${currentPositionOther.height}px`

        const translateY = currentPosition.top - newPosition.top
        const translateYOther = currentPositionOther.top - newPositionOther.top

        const speed = 0.4

        for (const placeholder of parentEl.querySelectorAll('.homepage-widget-placeholder')) {
          if (!isHtmlElement(placeholder)) continue
          placeholder.style.transition = `none`
          placeholder.style.transform = `translateY(${translateY}px)`
        }

        for (const otherPlaceholder of other.querySelectorAll('.homepage-widget-placeholder')) {
          if (!isHtmlElement(otherPlaceholder)) continue
          otherPlaceholder.style.transition = `none`
          otherPlaceholder.style.transform = `translateY(${translateYOther}px)`
        }
        const transition = (mul: number = 1) => {
          var val = `transform ${mul * speed}s cubic-bezier(1,.05,.7,1.36)`
          return val
        }

        setTimeout(function () {
          for (const placeholder of parentEl.querySelectorAll('.homepage-widget-placeholder')) {
            if (!isHtmlElement(placeholder)) continue
              placeholder.style.transition = transition()
              placeholder.style.transform = `translateY(0)`
          }

          for (const otherPlaceholder of other.querySelectorAll('.homepage-widget-placeholder')) {
            if (!isHtmlElement(otherPlaceholder)) continue
            const translateYAbs =  Math.abs(translateY)
            const translateYOtherAbs = Math.abs(translateYOther)
            if (translateYAbs < translateYOtherAbs) {
              otherPlaceholder.style.transition = transition(translateYAbs / translateYOtherAbs)
            } else {
              otherPlaceholder.style.transition = transition(translateYOtherAbs / translateYAbs)
            }
            otherPlaceholder.style.transform = `translateY(0)`
          }

          setTimeout(() => {
            parentEl.style.height = 'auto'
            other.style.height = 'auto'
          }, speed * 1000)
        })
        return
      }
  }

  render () {
    return null
  }
}


// high-order component that renders its first child if it exists.
// used as a conditional rendering proxy.
class PortalProxy extends Component<{children?: ComponentChildren}> {
  render (props: {children?: ComponentChildren}) {
    return Array.isArray(props.children) && props.children[0] || null
  }
}
